/**
 * WorkWise options and constants (contract sections 1-3 and 8).
 *
 * Server sizes are NOT defined here: since revision 2 they come from the admin
 * T-shirt sizes of the `workwise` product (Admin Settings -> Platform Config),
 * read through GET /products/sizes/. Everything marked TODO-confirm is a
 * placeholder the WorkWise team has not signed off on yet.
 *
 * Node-safe: no `@/` alias, no import.meta (the Vite mock layer imports it).
 */
import type {
  DbDiskRole,
  DbEngineVersion,
  DeploymentModel,
  DeploymentModelLabel,
  DiskType,
  ProvisioningStatus,
  SqlAuthentication,
  StorageWorkload,
  TempDbLocation,
  WorkwiseCicd,
  WorkwiseEnvironment,
  WorkwiseSku,
  ZoneChoice,
} from '../types/workwise'

/** WorkWise is detected by product slug only (the DB row name differs per install). */
export const WORKWISE_PRODUCT_SLUG = 'workwise'

export interface EnvironmentOption {
  value: WorkwiseEnvironment
  label: string
  /** Prefix letter used in VM names: p = production, d = dev and staging. */
  letter: 'p' | 'd'
  /** Token used in database names and the DB connection reference (TODO-confirm). */
  token: 'dev' | 'stg' | 'prd'
}

export const ENVIRONMENT_OPTIONS: EnvironmentOption[] = [
  { value: 'dev', label: 'Development', letter: 'd', token: 'dev' },
  { value: 'stg', label: 'Staging', letter: 'd', token: 'stg' },
  { value: 'production', label: 'Production', letter: 'p', token: 'prd' },
]

/** The only environments WorkWise offers in the shared Environment select. */
export const WORKWISE_ENVIRONMENTS: WorkwiseEnvironment[] = ['dev', 'stg', 'production']

export function isWorkwiseEnvironment(value: string): value is WorkwiseEnvironment {
  return (WORKWISE_ENVIRONMENTS as string[]).includes(value)
}

export const ENVIRONMENT_LABELS: Record<WorkwiseEnvironment, string> = {
  dev: 'Development',
  stg: 'Staging',
  production: 'Production',
}

/** The four T-shirt sizes and their labels, as on the existing size cards. */
export const WORKWISE_SKUS: WorkwiseSku[] = ['S', 'M', 'L', 'XL']
export const SKU_LABELS: Record<WorkwiseSku, string> = {
  S: 'Small',
  M: 'Medium',
  L: 'Large',
  XL: 'Extra Large',
}

/** Revision 3: one Deployment Model for the DB and the gateway together. */
export const DEPLOYMENT_MODEL_OPTIONS: Array<{ value: DeploymentModel; label: DeploymentModelLabel; summary: string }> = [
  { value: 'saas-hybrid', label: 'SaaS-Hybrid', summary: 'shared DB + gateway' },
  { value: 'dedicated', label: 'Dedicated', summary: 'own DB + gateway' },
]

export const DEPLOYMENT_MODEL_LABELS: Record<DeploymentModel, DeploymentModelLabel> = {
  'saas-hybrid': 'SaaS-Hybrid',
  dedicated: 'Dedicated',
}

/** The backend's 400 at gatewayServer.mode for a mixed topology. */
export const MODEL_MISMATCH_MESSAGE =
  'DB and Gateway must both be shared (SaaS-Hybrid) or both be dedicated (Dedicated).'

/** Where a SaaS-Hybrid network that is not recorded yet gets filled in. */
export const SHARED_SERVER_ADMIN_PATH = 'Django admin → WorkWise → Shared servers'

/** Revision 4 (R4-B): where the existing networks a SaaS-Hybrid app server can join are recorded. */
export const EXISTING_NETWORK_ADMIN_PATH = 'Django admin → WorkWise → Existing networks'

/** Revision 4 (R4-A): the backend's 400s at network.* when a key does not fit the deployment model. */
export const NETWORK_CIDR_REQUIRED_MESSAGE = 'Required for the Dedicated model.'
export const EXISTING_NETWORK_DEDICATED_MESSAGE = 'Not allowed for the Dedicated model.'
export const NETWORK_CIDR_SAAS_MESSAGE = 'Not allowed for the SaaS-Hybrid model.'

export const CICD_OPTIONS: Array<{ value: WorkwiseCicd; label: string }> = [
  { value: 'github_actions', label: 'GitHub Actions' },
  { value: 'azure_devops', label: 'Azure DevOps' },
]

/** Windows ids must start with "windows" (see isWindowsOs). */
export const OS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'windows-server-2022', label: 'Windows Server 2022' },
  { value: 'windows-server-2019', label: 'Windows Server 2019' },
  { value: 'ubuntu-22-04', label: 'Ubuntu 22.04 LTS' },
]

/** TODO-confirm: default OS when the admin size has no os_type. */
export const DEFAULT_OS = 'windows-server-2022'

export function isWindowsOs(os: string): boolean {
  return os.toLowerCase().startsWith('windows')
}

/**
 * Revision 6: the admin user of every WorkWise VM. Nothing is entered in the
 * wizard: a password is generated for each VM during deployment and stored in
 * Key Vault as <vm name>-admin-password.
 */
export const WORKWISE_ADMIN_USERNAME = 'Cloudops_admin'

/** The OS id the app server defaults to for an admin size's os_type. */
export function osIdForAdminOsType(osType: string | null | undefined): string {
  const value = (osType ?? '').toLowerCase()
  if (value.startsWith('windows')) return 'windows-server-2022'
  if (value.startsWith('linux') || value.startsWith('ubuntu')) return 'ubuntu-22-04'
  return DEFAULT_OS
}

export const DISK_TYPES: DiskType[] = [
  'Standard_LRS',
  'StandardSSD_LRS',
  'StandardSSD_ZRS',
  'Premium_LRS',
  'Premium_ZRS',
  'PremiumV2_LRS',
  'UltraSSD_LRS',
]

export const ZONE_OPTIONS: Array<{ value: ZoneChoice; label: string }> = [
  { value: '', label: 'No zone' },
  { value: '1', label: 'Zone 1' },
  { value: '2', label: 'Zone 2' },
  { value: '3', label: 'Zone 3' },
]

/**
 * A dedicated DB offers one SQL image only, like Azure's "SQL virtual machine" blade with a single
 * choice: SQL Server 2022 Standard on Windows Server 2022. The payload always carries engineVersion
 * '2022' (2019 is no longer valid), so the wizard shows the image read-only.
 */
export const DB_ENGINE_VERSION: DbEngineVersion = '2022'
export const DB_IMAGE_LABEL = 'SQL Server 2022 Standard on Windows Server 2022'

/** TODO-confirm: default disk type for new disks (production). */
export const DEFAULT_DISK_TYPE: DiskType = 'Premium_LRS'

/**
 * Default disk type outside production. The RnD subscriptions (dev and staging) have the Azure Policy
 * Deny-Premium-SKU-Disks, which refuses Premium and Ultra disks (OS and data), so a new disk there is Standard SSD.
 */
export const NON_PRODUCTION_DISK_TYPE: DiskType = 'StandardSSD_LRS'

/** TODO-confirm: default size of a new data disk ("+ Add Disk"), GB. */
export const DEFAULT_APP_DATA_DISK_GB = 256

/** Azure's disk size limits (GB) and the cap on the disks of one server (the backend's MAX_DISKS). */
export const DISK_SIZE_MIN_GB = 4
export const DISK_SIZE_MAX_GB = 32767
export const MAX_DISKS = 16

// ─── SQL Server authentication (a dedicated DB) ──────────────────────────────

export const SQL_AUTH_OPTIONS: Array<{ value: SqlAuthentication; label: string }> = [
  { value: 'windows', label: 'Windows authentication' },
  { value: 'sql', label: 'SQL Server authentication' },
]
export const DEFAULT_SQL_AUTHENTICATION: SqlAuthentication = 'windows'

/** Logins nobody may pick (compared case-insensitively); the backend refuses the same names. */
export const RESERVED_SQL_LOGINS: readonly string[] = [
  'sa', 'admin', 'administrator', 'root', 'sysadmin', 'guest', 'sql', 'dbo', 'public', 'test', 'user', 'owner',
  'server', 'support', 'sys',
]

/** No password is typed: the pipeline generates it and stores it in Key Vault under this name. */
export function sqlPasswordSecretName(vmName: string): string {
  return `${vmName.trim() || '<vm name>'}-sql-password`
}

// ─── SQL Server storage (a dedicated DB) ─────────────────────────────────────

/** Azure's "Storage optimized for". */
export const STORAGE_WORKLOAD_OPTIONS: Array<{ value: StorageWorkload; label: string }> = [
  { value: 'general', label: 'General purpose' },
  { value: 'oltp', label: 'Transactional processing (OLTP)' },
  { value: 'dw', label: 'Data warehousing' },
]
export const DEFAULT_STORAGE_WORKLOAD: StorageWorkload = 'general'

export const TEMPDB_LOCATION_OPTIONS: Array<{ value: TempDbLocation; label: string }> = [
  { value: 'local', label: "Local SSD (the VM's temporary drive D:)" },
  { value: 'disks', label: 'Dedicated disks' },
]

/** The role rows of the "SQL Server storage" panel, in the order the disks are sent. */
export const DB_DISK_ROLES: DbDiskRole[] = ['data', 'log', 'tempdb']
export const DB_ROLE_TITLES: Record<DbDiskRole, string> = {
  data: 'Data storage',
  log: 'Log storage',
  tempdb: 'TempDB storage',
}

/** Where the pipeline mounts each role (Azure's defaults for the SQL storage configuration). */
export const DB_VOLUMES: Record<DbDiskRole, string> & { tempdbLocal: string } = {
  data: 'F:\\data',
  log: 'G:\\log',
  tempdb: 'H:\\tempDb',
  tempdbLocal: 'D:\\tempDb',
}

/** The group a row starts with. */
export interface DbDiskGroupDefault {
  type: DiskType
  sizeGb: number
  count: number
}

/**
 * Defaults: data = 512 GB x 1, log = 256 GB x 1 (both Premium_LRS), TempDB on the local SSD. The TempDB
 * disk values apply once "Dedicated disks" is chosen (TODO-confirm: 128 GB x 1).
 */
export const DEFAULT_DB_STORAGE: {
  data: DbDiskGroupDefault
  log: DbDiskGroupDefault
  tempdb: DbDiskGroupDefault & { location: TempDbLocation }
} = {
  data: { type: DEFAULT_DISK_TYPE, sizeGb: 512, count: 1 },
  log: { type: DEFAULT_DISK_TYPE, sizeGb: 256, count: 1 },
  tempdb: { location: 'local', type: DEFAULT_DISK_TYPE, sizeGb: 128, count: 1 },
}

/**
 * Premium SSD v2 and Ultra disks exist only in an availability zone: a DB that uses one (in any group)
 * needs its Availability Zone set.
 */
export const ZONE_ONLY_DISK_TYPES: readonly DiskType[] = ['PremiumV2_LRS', 'UltraSSD_LRS']

/** The backend's 400 at dbServer.sku / gatewayServer.sku when a dedicated size differs from the step-2 size. */
export function dedicatedSizeMessage(appSku: string): string {
  return `Dedicated servers use the size chosen in Configure Sizes (${appSku}).`
}

export const DEFAULT_SMTP_PORT = 587

/**
 * The Application (ERP) step (step 4) starts out skipped while the infrastructure is being tested: Next on step 3
 * goes straight to Review & Deploy and the request carries no `app`. The step itself is untouched; its first toggle
 * (or "Add application settings" on Review) turns the settings back on. Set to false to start with the step active.
 */
export const DEFAULT_SKIP_APPLICATION = true

/** Limits taken from the contract. */
export const MAX_NSG_RULES = 20
export const MAX_EXTRA_TAGS = 15
export const CUSTOMER_NAME_MIN = 3
export const CUSTOMER_NAME_MAX = 50
export const SLUG_MIN = 2
export const SLUG_MAX = 40

/** Backend rejects these ports when the source is open to the internet. */
export const BLOCKED_NSG_PORTS = [22, 3389, 1433, 5432]
export const OPEN_NSG_SOURCES = ['*', 'internet', '0.0.0.0/0']

/** Default inbound rule offered for a dedicated gateway. TODO-confirm. */
export const DEFAULT_NSG_RULES = [{ name: 'allow-https', port: 443, source: '*' }]

/**
 * Tag keys the portal itself sets; the backend rejects them in `tags.extra`.
 * Revision 3 dropped cost centre and expiry date, so only the owner is left.
 */
export const RESERVED_TAG_KEYS = ['owner']

/** TODO-confirm: Key Vault that holds the per-customer DB connection string. */
export function workwiseKeyVault(token: string): string {
  return `kv-workwise-${token}`
}

// ─── Provisioning status (contract section 4.7) ──────────────────────────────

export const PROVISIONING_STATUSES: ProvisioningStatus[] = [
  'Pending',
  'Provisioning',
  'Deployed',
  'Failed',
  'Destroying',
  'Destroyed',
]

const PROVISIONING_STAGES = [
  'validating',
  'provisioning_db_users',
  'provisioning_network',
  'provisioning_infra',
  'deploying_app',
  'configuring_haproxy',
  'configuring_cloudflare',
  'post_deploy_config',
  'configuring_integrations',
  'configuring',
  'running_health_checks',
  'switching_traffic',
]

/** Maps a raw DeploymentRequest.status to the coarse provisioning group. */
export function provisioningStatusOf(status: string): ProvisioningStatus {
  if (status === 'pending') return 'Pending'
  if (status === 'completed') return 'Deployed'
  if (status === 'failed' || status === 'rolled_back' || status === 'cancelled') return 'Failed'
  if (status === 'destroying') return 'Destroying'
  if (status === 'destroyed') return 'Destroyed'
  if (PROVISIONING_STAGES.includes(status)) return 'Provisioning'
  return 'Pending'
}
